import React, { useMemo } from 'react';
import { Box, createStyles, makeStyles } from '@material-ui/core';
import PopupSelectMenu from './PopupSelectMenu';

export const colorTypes: (string | null)[] = [
  '#7ED321',
  '#00A7F7',
  '#B057FF',
  '#FF5ABB',
  '#F55323',
  '#B29377',
  '#FFAC00',
  '#00B8A9',
  '#FC8181',
  '#7F9CF5',
  null,
];

export type ColorTypeValue = (typeof colorTypes)[number];

const REMOVE_COLOR_ID = 'REMOVE_COLOR';

export interface PopupColorSelectMenuProps {
  selectedColor?: string | null;
  editable?: boolean;
  onSelect: (color: string | null) => Promise<void> | void;
  renderTrigger: (params: { onClick: (event: React.MouseEvent<any>) => void }) => React.ReactNode;
}

const useStyles = makeStyles(theme =>
  createStyles({
    menuItem: {
      paddingTop: 0,
      paddingBottom: 0,
      minHeight: 0,
    },
    colorSquare: {
      width: '16rem',
      height: '2rem',
      margin: '0.25rem 0.5rem',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      border: '2px solid transparent',
      '&:hover': {
        border: '2px solid black',
      },
    },
  })
);

const PopupColorSelectMenu: React.FC<PopupColorSelectMenuProps> = ({
  selectedColor,
  editable = true,
  onSelect,
  renderTrigger,
}) => {
  const classes = useStyles();

  const options = useMemo(
    () =>
      colorTypes.map(c => ({
        id: (c || REMOVE_COLOR_ID) as string,
        label: c || 'Remove',
      })),
    []
  );

  const handleSelect = async (id: string) => {
    const nextColor = id === REMOVE_COLOR_ID ? null : id;
    await onSelect(nextColor);
  };

  return (
    <PopupSelectMenu
      options={options}
      selectedId={selectedColor ?? undefined}
      editable={editable}
      onSelect={handleSelect}
      menuTitle="Choose procedure color"
      showSearch={false}
      menuItemClassName={classes.menuItem}
      renderTrigger={renderTrigger}
      renderOption={({ option, isSelected }) => {
        const actualColor = option.id === REMOVE_COLOR_ID ? null : (option.id as string);
        return (
          <Box
            className={classes.colorSquare}
            style={{
              backgroundColor: actualColor || 'transparent',
              borderColor: isSelected ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
            }}
          >
            {actualColor ? null : 'Remove'}
          </Box>
        );
      }}
    />
  );
};

export default PopupColorSelectMenu;
